/**
 * TEMPORARY DEV/TEST TOOL — simulate all major channels as connected (UI + save-path testing).
 *
 * Trigger: ?simulate=on | ?simulate=off on any page (stored in sessionStorage for the tab).
 * Remove later: delete this file, remove client.ts interceptor, remove main.jsx + NavigationTracker init calls.
 */
import { CHANNEL_META } from '@/components/budgettargets/budgetConfig';

const SESSION_STORAGE_KEY = 'rimora_dev_simulate_all_channels';

export const DEV_SIMULATED_CONNECTED_ID = 'dev-simulated';

/** Real OAuth connection — dev-simulated never counts (Gate 7.1j, mirrors backend law). */
export function isProductionConnectedChannel(ch) {
  if (!ch || ch.is_active === false) return false;
  const id = ch.connected_account_id;
  return typeof id === 'string' && id.length > 0 && id !== DEV_SIMULATED_CONNECTED_ID;
}

/** UI-visible channel tab: connected, or any channel when simulate is on. */
export function isUiVisibleChannel(platform, connectedChannels) {
  if (!platform) return false;
  const list = connectedChannels || [];
  if (isSimulateAllChannelsEnabled()) {
    return list.some((ch) => ch.platform === platform);
  }
  return list.some((ch) => ch.platform === platform && isProductionConnectedChannel(ch));
}

export const SIMULATED_PLATFORMS = [
  'google_ads',
  'meta_ads',
  'bing_ads',
  'linkedin_ads',
];

/** @returns {boolean} Whether the simulate tool is enabled in this build (Gate 7.1j J3). */
export function isSimulateFeatureAllowed() {
  if (import.meta.env.DEV) return true;
  return import.meta.env.VITE_ALLOW_CHANNEL_SIMULATE === 'true';
}

/** @returns {boolean} */
export function isSimulateAllChannelsEnabled() {
  if (!isSimulateFeatureAllowed()) return false;
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function setSimulateAllChannelsEnabled(enabled) {
  try {
    if (enabled) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable — no-op
  }
}

/**
 * Read ?simulate=on|off from the URL, persist to sessionStorage, strip the param.
 * Reloads when the flag changes so channel lists refetch with the new override.
 *
 * @param {string} [search]
 */
export function initSimulateChannelsFromUrl(search = window.location.search) {
  if (!isSimulateFeatureAllowed()) {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // no-op
    }
    return;
  }

  const params = new URLSearchParams(search);
  const simulate = params.get('simulate');
  if (simulate !== 'on' && simulate !== 'off') return;

  setSimulateAllChannelsEnabled(simulate === 'on');

  params.delete('simulate');
  const qs = params.toString();
  const cleanUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  window.location.replace(cleanUrl);
}

function simulatedChannelId(profileId, platform) {
  return `dev-sim-${profileId}-${platform}`;
}

function makeSimulatedChannel(profileId, platform) {
  const meta = CHANNEL_META[platform] || {};
  const label = meta.label || platform;
  return {
    id: simulatedChannelId(profileId, platform),
    profile_id: profileId,
    platform,
    connected_account_id: 'dev-simulated',
    is_active: true,
    naming_convention_notes: null,
    connected_account: {
      platform_account_name: label,
    },
  };
}

/**
 * When simulate-all-channels is on, ensure all SIMULATED_PLATFORMS appear connected.
 * When off, returns channels unchanged.
 *
 * @param {Array<Record<string, unknown>> | null | undefined} channels
 * @param {string | null | undefined} profileId
 * @returns {Array<Record<string, unknown>>}
 */
export function applyDevConnectedChannelsOverride(channels, profileId) {
  if (!isSimulateFeatureAllowed() || !isSimulateAllChannelsEnabled() || !profileId) {
    return Array.isArray(channels) ? channels : [];
  }

  const list = Array.isArray(channels) ? [...channels] : [];
  const indexByPlatform = new Map();
  for (let i = 0; i < list.length; i++) {
    const platform = list[i]?.platform;
    if (platform) indexByPlatform.set(platform, i);
  }

  for (const platform of SIMULATED_PLATFORMS) {
    const idx = indexByPlatform.get(platform);
    if (idx !== undefined) {
      const existing = list[idx];
      if (!existing.connected_account_id) {
        const meta = CHANNEL_META[platform] || {};
        list[idx] = {
          ...existing,
          connected_account_id: 'dev-simulated',
          is_active: existing.is_active !== false,
          connected_account: existing.connected_account ?? {
            platform_account_name: meta.label || platform,
          },
        };
      }
      continue;
    }
    list.push(makeSimulatedChannel(profileId, platform));
  }

  return list;
}
